/**
 * Left column of EssayGrader: submission form + previous submissions list.
 */
import React from 'react';
import { Send, Loader2, BarChart3, Clock, User, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

export function EssaySubmitForm({
  unit,
  studentName, setStudentName,
  essayText, setEssayText,
  promptIndex, setPromptIndex,
  submitting,
  onSubmit,
}) {
  const essayPrompts = unit?.essay_prompts || [];
  const wordCount = essayText.trim() ? essayText.trim().split(/\s+/).length : 0;
  return (
    <Card className="p-5 border border-stone-200 bg-white" data-testid="essay-submit-form">
      <h3 className="font-semibold text-stone-800 mb-4 flex items-center gap-2">
        <Send className="h-4 w-4" /> Submit Essay for Grading
      </h3>
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">Student Name</label>
          <input
            type="text"
            value={studentName}
            onChange={(e) => setStudentName(e.target.value)}
            className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
            placeholder="Enter student name"
            data-testid="student-name-input"
          />
        </div>
        {essayPrompts.length > 0 && (
          <div>
            <label className="block text-sm font-medium text-stone-700 mb-1">Essay Prompt</label>
            <select
              value={promptIndex}
              onChange={(e) => setPromptIndex(Number(e.target.value))}
              className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
              data-testid="essay-prompt-select"
            >
              {essayPrompts.map((p, i) => (
                <option key={`prompt-${i}-${(typeof p === 'object' ? p.title : p)?.slice(0, 30) || i}`} value={i}>
                  {typeof p === 'object' ? (p.title || `Prompt ${i + 1}`) : `Prompt ${i + 1}`}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-stone-700 mb-1">
            Essay Text <span className="text-stone-400">({wordCount} words)</span>
          </label>
          <textarea
            value={essayText}
            onChange={(e) => setEssayText(e.target.value)}
            rows={12}
            className="w-full px-3 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 resize-y font-mono leading-relaxed"
            placeholder="Paste or type the student's essay here..."
            data-testid="essay-text-input"
          />
        </div>
        <Button
          type="submit"
          className="w-full bg-stone-800 hover:bg-stone-900"
          disabled={submitting}
          data-testid="submit-essay-btn"
        >
          {submitting ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Analyzing Essay...</>
          ) : (
            <><BarChart3 className="h-4 w-4 mr-2" /> Grade Essay</>
          )}
        </Button>
      </form>
    </Card>
  );
}

export function SubmissionHistoryList({ submissions, activeId, onView, onDelete }) {
  if (!submissions.length) return null;
  return (
    <Card className="p-5 border border-stone-200 bg-white" data-testid="submission-history">
      <h3 className="font-semibold text-stone-800 mb-3 flex items-center gap-2">
        <Clock className="h-4 w-4" /> Previous Submissions ({submissions.length})
      </h3>
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {submissions.map((s) => (
          <div
            key={s.id}
            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
              activeId === s.id ? 'border-amber-400 bg-amber-50' : 'border-stone-200 hover:bg-stone-50'
            }`}
            onClick={() => onView(s.id)}
            data-testid={`submission-item-${s.id}`}
          >
            <div className="min-w-0">
              <p className="text-sm font-medium text-stone-800 truncate flex items-center gap-1.5">
                <User className="h-3.5 w-3.5 shrink-0" /> {s.student_name}
              </p>
              <p className="text-xs text-stone-500">
                {s.word_count} words &middot; {s.grade_result?.letter_grade || s.status}
              </p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => { e.stopPropagation(); onDelete(s.id); }}
              data-testid={`delete-submission-${s.id}`}
            >
              <Trash2 className="h-3.5 w-3.5 text-stone-400" />
            </Button>
          </div>
        ))}
      </div>
    </Card>
  );
}
